import React, { useState, useCallback } from 'react';
import { 
  ChevronLeft, Play, Copy, Download, Save, Settings2, 
  CircleDot, Wrench, ChevronDown, ChevronUp, Plus,
  Trash2, Edit3, RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  controllerConfig, 
  materialPresets, 
  turningOperations,
  generateTurningCode,
  formatGCodeLine 
} from '@/utils/gcode';
import { uncThreads, unfThreads, metricCoarseThreads, metricFineThreads, getThreadList } from '@/utils/threadLibrary';
import { machinePresets } from '@/utils/turnmill';
import ProgramBuilder from '@/components/ProgramBuilder';

const TurningPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single');
  const [controller, setController] = useState('fanuc');
  const [machinePreset, setMachinePreset] = useState('samsung-sl25');
  const [machineConfig, setMachineConfig] = useState('2axis');
  const [selectedOp, setSelectedOp] = useState('od-roughing');
  const [setupExpanded, setSetupExpanded] = useState(false);
  const [gcode, setGcode] = useState([]);
  const [copied, setCopied] = useState(false);

  // Workpiece setup state (inches)
  const [setup, setSetup] = useState({
    stockDia: 2.0,
    stockLength: 4.0,
    finishDia: 1.5,
    finishLength: 3.75,
    material: 'alu-6061',
    safeX: 4.0,
    safeZ: 0.5,
  });

  // Operation parameters
  const [opParams, setOpParams] = useState({});
  
  // Program settings (inches)
  const [settings, setSettings] = useState({
    programNum: 1,
    programName: '',
    tool: 1,
    offset: 1,
    spindleMode: 'G97',
    speed: 1200,
    roughFeed: 0.008,
    finishFeed: 0.004,
    addHeader: true,
    safeZ: 0.5,
    safeX: 4.0,
    doc: 0.080,
  });

  // Update operation params when operation changes
  React.useEffect(() => {
    const op = turningOperations[selectedOp];
    if (op) {
      const defaults = {};
      op.fields.forEach(field => {
        defaults[field.id] = field.default;
      });
      setOpParams(defaults);
    }
  }, [selectedOp]);

  const handleParamChange = (id, value) => {
    // Handle numeric values and string values (for dropdowns)
    const numValue = parseFloat(value);
    const newValue = isNaN(numValue) ? value : numValue;
    
    // Handle thread selection auto-populate
    if (id === 'threadDesignation' && selectedOp === 'od-threading') {
      const threadSystem = opParams.threadSystem || 'imperial';
      const threadSeries = opParams.threadSeries || 'UNC';
      const threads = getThreadList(threadSystem, threadSeries);
      const selectedThread = threads.find(t => t.designation === value);
      
      if (selectedThread) {
        setOpParams(prev => ({
          ...prev,
          threadDesignation: value,
          majorDia: selectedThread.majorDia,
          minorDia: selectedThread.minorDiaMale || selectedThread.minorDia,
          pitch: selectedThread.pitch,
          tpi: selectedThread.tpi || Math.round(1 / selectedThread.pitch)
        }));
        return;
      }
    }
    
    // Handle thread system change - reset series and thread size
    if (id === 'threadSystem' && selectedOp === 'od-threading') {
      const newSeries = value === 'imperial' ? 'UNC' : 'coarse';
      const threads = getThreadList(value, newSeries);
      
      if (threads.length > 0) {
        const firstThread = threads[0];
        setOpParams(prev => ({
          ...prev,
          threadSystem: value,
          threadSeries: newSeries,
          threadDesignation: firstThread.designation,
          majorDia: firstThread.majorDia,
          minorDia: firstThread.minorDiaMale || firstThread.minorDia,
          pitch: firstThread.pitch,
          tpi: firstThread.tpi || Math.round(1 / firstThread.pitch)
        }));
        return;
      }
    }
    
    // Handle thread series change - reset thread size
    if (id === 'threadSeries' && selectedOp === 'od-threading') {
      const threadSystem = opParams.threadSystem || 'imperial';
      const threads = getThreadList(threadSystem, value);
      
      if (threads.length > 0) {
        const firstThread = threads[0];
        setOpParams(prev => ({
          ...prev,
          threadSeries: value,
          threadDesignation: firstThread.designation,
          majorDia: firstThread.majorDia,
          minorDia: firstThread.minorDiaMale || firstThread.minorDia,
          pitch: firstThread.pitch,
          tpi: firstThread.tpi || Math.round(1 / firstThread.pitch)
        }));
        return;
      }
    }
    
    setOpParams(prev => ({ ...prev, [id]: newValue }));
  };

  const handleSettingChange = (id, value) => {
    setSettings(prev => ({ ...prev, [id]: value }));
  };

  const generateCode = useCallback(() => {
    const code = generateTurningCode(selectedOp, opParams, settings, controller);
    setGcode(code);
  }, [selectedOp, opParams, settings, controller]);

  const copyCode = async () => {
    const text = gcode.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for clipboard permission denied
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const downloadCode = () => {
    const text = gcode.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `O${String(settings.programNum).padStart(4, '0')}_${settings.programName || 'turning'}.nc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'single', label: 'Single Op' },
    { id: 'builder', label: 'Program Builder' },
    { id: 'tools', label: 'Tools Guide' },
  ];

  return (
    <div className="min-h-screen bg-industrial-bg">
      {/* Sub Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border">
        <div className="flex items-center justify-between px-4 py-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          <h2 className="font-heading font-bold text-industrial-success flex items-center gap-2">
            <CircleDot className="w-5 h-5" />
            CNC TURNING
          </h2>

          <select
            value={controller}
            onChange={(e) => setController(e.target.value)}
            className="bg-industrial-surface-highlight border border-industrial-border rounded px-2 py-1 text-sm text-industrial-text"
            data-testid="controller-select"
          >
            {Object.entries(controllerConfig).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.name}</option>
            ))}
          </select>
        </div>

        {/* Machine Preset Row */}
        <div className="flex items-center justify-center gap-2 px-4 py-2 border-t border-industrial-border bg-industrial-surface-highlight overflow-x-auto">
          {Object.entries(machinePresets).filter(([, m]) => m.type !== 'mill').map(([key, machine]) => (
            <button
              key={key}
              onClick={() => {
                setMachinePreset(key);
                setController(machine.controller);
                if (key === 'samsung-sl25') setMachineConfig('2axis');
                else if (key === 'hyundai-2600y') setMachineConfig('5axis-turnmill');
              }}
              data-testid={`preset-${key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                machinePreset === key
                  ? 'bg-industrial-success text-black'
                  : 'bg-industrial-surface border border-industrial-border text-industrial-text-secondary hover:border-industrial-success'
              }`}
            >
              {machine.name}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-t border-industrial-border">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-industrial-safety border-b-2 border-industrial-safety bg-industrial-safety/10'
                  : 'text-industrial-text-secondary hover:text-industrial-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Workpiece Setup Panel */}
      <div className="mx-4 mt-4">
        <button
          onClick={() => setSetupExpanded(!setupExpanded)}
          className="w-full flex items-center justify-between p-4 bg-industrial-surface border border-industrial-border rounded-t-lg"
          data-testid="setup-toggle"
        >
          <span className="flex items-center gap-2 font-medium text-industrial-readout">
            <Settings2 className="w-4 h-4" />
            Workpiece & Material Setup
          </span>
          {setupExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {setupExpanded && (
          <div className="bg-industrial-surface border-x border-b border-industrial-border rounded-b-lg p-4 space-y-4 animate-in">
            {/* Stock Dimensions */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Stock Diameter (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.stockDia}
                  onChange={(e) => setSetup(s => ({ ...s, stockDia: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Stock Length (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.stockLength}
                  onChange={(e) => setSetup(s => ({ ...s, stockLength: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
            </div>

            {/* Material */}
            <div>
              <label className="text-xs text-industrial-text-secondary mb-1 block">Material</label>
              <select
                value={setup.material}
                onChange={(e) => setSetup(s => ({ ...s, material: e.target.value }))}
                className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
              >
                {Object.entries(materialPresets).map(([key, mat]) => (
                  <option key={key} value={key}>{mat.name}</option>
                ))}
              </select>
              {materialPresets[setup.material] && (
                <div className="mt-2 flex gap-4 text-xs text-industrial-text-secondary">
                  <span>SFM: <span className="text-industrial-readout">{materialPresets[setup.material].sfm}</span></span>
                  <span>Feed: <span className="text-industrial-readout">{materialPresets[setup.material].feed}</span></span>
                </div>
              )}
            </div>

            {/* Safe Distances */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Safe X (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.safeX}
                  onChange={(e) => setSetup(s => ({ ...s, safeX: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Safe Z (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.safeZ}
                  onChange={(e) => setSetup(s => ({ ...s, safeZ: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-4">
        {activeTab === 'single' && (
          <div className="space-y-4 animate-in">
            {/* Operation Selection */}
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-3">Select Operation</h3>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(turningOperations).map(([key, op]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedOp(key)}
                    data-testid={`op-${key}`}
                    className={`p-3 rounded-lg border text-center transition-all text-sm ${
                      selectedOp === key
                        ? 'bg-industrial-success/20 border-industrial-success text-industrial-success'
                        : 'bg-industrial-surface-highlight border-industrial-border text-industrial-text hover:border-industrial-safety'
                    }`}
                  >
                    {op.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Operation Parameters */}
            {turningOperations[selectedOp] && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
                <h3 className="font-heading font-semibold text-industrial-text mb-1">
                  {turningOperations[selectedOp].name} Parameters
                </h3>
                <p className="text-xs text-industrial-text-secondary mb-4">
                  {turningOperations[selectedOp].description}
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  {turningOperations[selectedOp].fields.map(field => (
                    <div key={field.id}>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">
                        {field.label}
                      </label>
                      {field.type === 'threadSelect' ? (
                        <select
                          value={opParams[field.id] ?? field.default}
                          onChange={(e) => handleParamChange(field.id, e.target.value)}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                          data-testid={`param-${field.id}`}
                        >
                          {getThreadList(opParams.threadSystem || 'imperial', opParams.threadSeries || 'UNC').map(thread => (
                            <option key={thread.designation} value={thread.designation}>
                              {thread.designation} ({thread.tpi || Math.round(1/thread.pitch)} TPI)
                            </option>
                          ))}
                        </select>
                      ) : field.id === 'threadSeries' && field.type === 'select' ? (
                        <select
                          value={opParams[field.id] ?? field.default}
                          onChange={(e) => handleParamChange(field.id, e.target.value)}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                          data-testid={`param-${field.id}`}
                        >
                          {(opParams.threadSystem === 'metric' 
                            ? ['coarse', 'fine'] 
                            : ['UNC', 'UNF']
                          ).map(opt => (
                            <option key={opt} value={opt}>{opt.toUpperCase()}</option>
                          ))}
                        </select>
                      ) : field.type === 'select' && field.options ? (
                        <select
                          value={opParams[field.id] ?? field.default}
                          onChange={(e) => handleParamChange(field.id, e.target.value)}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                          data-testid={`param-${field.id}`}
                        >
                          {field.options.map(opt => {
                            // Handle both string options and object options
                            const value = typeof opt === 'string' ? opt : opt.value;
                            const label = typeof opt === 'string' ? opt.toUpperCase() : opt.label;
                            return <option key={value} value={value}>{label}</option>;
                          })}
                        </select>
                      ) : field.options ? (
                        <select
                          value={opParams[field.id] ?? field.default}
                          onChange={(e) => handleParamChange(field.id, e.target.value)}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                          data-testid={`param-${field.id}`}
                        >
                          {field.options.map(opt => {
                            const value = typeof opt === 'string' ? opt : opt.value;
                            const label = typeof opt === 'string' ? opt : opt.label;
                            return <option key={value} value={value}>{label}</option>;
                          })}
                        </select>
                      ) : (
                        <input
                          type="number"
                          step="0.001"
                          value={opParams[field.id] ?? field.default}
                          onChange={(e) => handleParamChange(field.id, e.target.value)}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                          data-testid={`param-${field.id}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cutting Parameters */}
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-3">Cutting Parameters</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Tool #</label>
                  <input
                    type="number"
                    value={settings.tool}
                    onChange={(e) => handleSettingChange('tool', parseInt(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Speed (RPM)</label>
                  <input
                    type="number"
                    value={settings.speed}
                    onChange={(e) => handleSettingChange('speed', parseInt(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Rough Feed (IPR)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={settings.roughFeed}
                    onChange={(e) => handleSettingChange('roughFeed', parseFloat(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Finish Feed (IPR)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={settings.finishFeed}
                    onChange={(e) => handleSettingChange('finishFeed', parseFloat(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateCode}
              data-testid="generate-btn"
              className="w-full py-4 bg-industrial-safety text-black font-heading font-bold uppercase tracking-wider rounded-lg shadow-button active:shadow-none active:translate-y-1 transition-all flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Generate G-Code
            </button>

            {/* G-Code Output */}
            {gcode.length > 0 && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-industrial-surface-highlight border-b border-industrial-border">
                  <span className="text-sm font-medium text-industrial-text">Generated G-Code</span>
                  <div className="flex gap-2">
                    <button
                      onClick={copyCode}
                      data-testid="copy-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors"
                    >
                      <Copy className={`w-4 h-4 ${copied ? 'text-industrial-success' : 'text-industrial-text-secondary'}`} />
                    </button>
                    <button
                      onClick={downloadCode}
                      data-testid="download-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors"
                    >
                      <Download className="w-4 h-4 text-industrial-text-secondary" />
                    </button>
                  </div>
                </div>
                <div className="p-4 max-h-80 overflow-y-auto bg-[#1e1e1e]">
                  <pre className="font-mono text-sm leading-relaxed">
                    {gcode.map((line, i) => (
                      <div 
                        key={i} 
                        className="flex"
                        dangerouslySetInnerHTML={{ 
                          __html: `<span class="gcode-line-number w-8 inline-block">${String(i + 1).padStart(3)}</span>${formatGCodeLine(line)}`
                        }}
                      />
                    ))}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'builder' && (
          <ProgramBuilder 
            type="turning" 
            controller={controller}
            machineConfig={machineConfig}
          />
        )}

        {activeTab === 'tools' && (
          <div className="space-y-4 animate-in">
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-4">Turning Operations Reference</h3>
              
              {Object.entries(turningOperations).map(([key, op]) => (
                <div key={key} className="border-b border-industrial-border last:border-0 py-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-industrial-text">{op.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-industrial-success/20 text-industrial-success rounded">
                      {op.cycle}
                    </span>
                  </div>
                  <p className="text-sm text-industrial-text-secondary">{op.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-4">Controller Comparison</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-industrial-border">
                      <th className="text-left py-2 text-industrial-text-secondary">Feature</th>
                      <th className="text-left py-2 text-industrial-readout">FANUC</th>
                      <th className="text-left py-2 text-industrial-readout">Siemens</th>
                      <th className="text-left py-2 text-industrial-readout">Haas</th>
                    </tr>
                  </thead>
                  <tbody className="text-industrial-text">
                    <tr className="border-b border-industrial-border">
                      <td className="py-2">OD Roughing</td>
                      <td>G71</td>
                      <td>CYCLE95</td>
                      <td>G71</td>
                    </tr>
                    <tr className="border-b border-industrial-border">
                      <td className="py-2">Face Roughing</td>
                      <td>G72</td>
                      <td>CYCLE95</td>
                      <td>G72</td>
                    </tr>
                    <tr className="border-b border-industrial-border">
                      <td className="py-2">Finishing</td>
                      <td>G70</td>
                      <td>CYCLE95</td>
                      <td>G70</td>
                    </tr>
                    <tr>
                      <td className="py-2">Threading</td>
                      <td>G76</td>
                      <td>CYCLE97</td>
                      <td>G76</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TurningPage;
